/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useLayoutEffect, useRef } from "react";
import { CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import type { Toast as ToastType } from "../../contexts/ToastContext";

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const flyRef = useRef<HTMLDivElement>(null);

  // Fly in from the click that caused it: measure where the toast landed, start it at the
  // pointer (shrunk and translucent), then release it to its resting place. Keyboard and
  // programmatic toasts, and reduced-motion users, get the plain slide.
  useLayoutEffect(() => {
    const el = flyRef.current;
    if (el === null || toast.origin === null) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const dx = toast.origin.x - (rect.left + rect.width / 2);
    const dy = toast.origin.y - (rect.top + rect.height / 2);
    el.classList.remove("animate-toast-in");
    el.style.transform = `translate(${dx}px, ${dy}px) scale(0.35)`;
    el.style.opacity = "0.4";
    el.style.transition = "none";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transition =
          "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease-out";
        el.style.transform = "";
        el.style.opacity = "";
      });
    });
  }, [toast.origin]);

  const tone =
    toast.type === "error"
      ? { icon: <TriangleAlert size={16} className="text-error" />, chip: "bg-error-bg" }
      : toast.type === "info"
        ? { icon: <Info size={16} className="text-accent" />, chip: "bg-info-bg" }
        : { icon: <CircleCheck size={16} className="text-success" />, chip: "bg-success-bg" };

  return (
    <div ref={flyRef} role="status" className="w-full max-w-sm animate-toast-in will-change-transform">
      <div className="glass-strong flex items-center gap-3 rounded-2xl py-3 pl-3 pr-4 shadow-elevated">
        <span
          className={`grid size-8 shrink-0 place-items-center rounded-xl ${tone.chip}`}
        >
          {tone.icon}
        </span>
        <p className="flex-1 text-sm leading-relaxed text-text-primary">{toast.message}</p>
        <button
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 cursor-pointer rounded-lg p-1 text-text-tertiary transition-colors duration-200 hover:bg-bg-hover hover:text-text-primary"
          aria-label="Dismiss"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastType[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      className="fixed right-4 left-4 sm:left-auto sm:right-4 z-50 flex flex-col items-center sm:items-end gap-3 pointer-events-none"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto w-full sm:w-auto">
          <Toast toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
}
